import React from 'react';
import { formatDate } from '../../../lib/utils';
import { Badge } from '../../../components/ui/badge';
import { Button } from '../../../components/ui/button';
import { Input } from '../../../components/ui/input';
import { Label } from '../../../components/ui/label';
import { Textarea } from '../../../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../../../components/ui/dialog';
import { History, Shield, Clock, Edit3, Send } from 'lucide-react';
import { statusColors, statusLabels } from './claimConstants';

export function RevisionDialog({ open, onOpenChange, comment, setComment, fieldComments, setFieldComments, onSubmit }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Request Revision</DialogTitle>
          <DialogDescription>Specify what needs to be corrected. The agent will be notified.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>General Comment</Label>
            <Textarea placeholder="Describe what needs to be revised..." value={comment} onChange={e => setComment(e.target.value)} />
          </div>
          <div className="space-y-2">
            <Label>Field-Specific Comments (Optional)</Label>
            <div className="grid grid-cols-2 gap-2">
              {['customer_full_name', 'customer_nric', 'spa_price', 'commission_percentage', 'documents'].map(field => (
                <Input key={field} placeholder={field.replace(/_/g, ' ')} value={fieldComments[field] || ''} onChange={e => setFieldComments({ ...fieldComments, [field]: e.target.value })} className="text-sm" />
              ))}
            </div>
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>Cancel</Button>
          <Button onClick={onSubmit} className="bg-orange-600 hover:bg-orange-700"><Send className="h-4 w-4 mr-1" /> Send Revision Request</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export function AuditTrailDialog({ open, onOpenChange, data }) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2"><History className="h-5 w-5" /> Audit Trail - {data?.claim_ref}</DialogTitle>
          <DialogDescription>Complete history of all actions taken on this claim</DialogDescription>
        </DialogHeader>
        {data && (
          <div className="space-y-6 py-4">
            {data.audit_trail && data.audit_trail.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Shield className="h-4 w-4 text-blue-600" /> Action Log</h4>
                <div className="space-y-3">
                  {data.audit_trail.map((entry, idx) => (
                    <div key={idx} className="p-3 bg-gray-50 rounded-lg border-l-4 border-blue-500">
                      <div className="flex items-center justify-between mb-1">
                        <Badge className={entry.action === 'approved' ? 'bg-green-500' : entry.action === 'rejected' ? 'bg-red-500' : entry.action === 'revision_requested' ? 'bg-orange-500' : 'bg-blue-500'}>
                          {entry.action.replace(/_/g, ' ').toUpperCase()}
                        </Badge>
                        <span className="text-xs text-gray-500">{formatDate(entry.timestamp)}</span>
                      </div>
                      <div className="text-sm">
                        <span className="font-medium">{entry.performed_by_name}</span>
                        <Badge variant="outline" className="ml-2 text-xs">{entry.performed_by_role?.toUpperCase()}</Badge>
                      </div>
                      {entry.notes && <p className="text-sm text-gray-600 mt-1">{entry.notes}</p>}
                      {entry.reason && <p className="text-sm text-red-600 mt-1">Reason: {entry.reason}</p>}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.status_history && data.status_history.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Clock className="h-4 w-4 text-gray-600" /> Status History</h4>
                <div className="space-y-2">
                  {data.status_history.map((entry, idx) => (
                    <div key={idx} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <div className="flex items-center gap-2">
                        <Badge className={statusColors[entry.status]}>{statusLabels[entry.status]}</Badge>
                        {entry.changed_by_role && <span className="text-xs text-gray-500">by {entry.changed_by_role.toUpperCase()}</span>}
                      </div>
                      <span className="text-xs text-gray-500">{formatDate(entry.changed_at)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}
            {data.revision_requests && data.revision_requests.length > 0 && (
              <div>
                <h4 className="font-semibold mb-3 flex items-center gap-2"><Edit3 className="h-4 w-4 text-orange-600" /> Revision Requests</h4>
                <div className="space-y-3">
                  {data.revision_requests.map((req, idx) => (
                    <div key={idx} className="p-3 bg-orange-50 rounded-lg">
                      <div className="flex items-center justify-between mb-2">
                        <div>
                          <span className="font-medium">{req.requested_by_name}</span>
                          {req.requested_by_role && <Badge variant="outline" className="ml-2 text-xs">{req.requested_by_role.toUpperCase()}</Badge>}
                        </div>
                        <span className="text-xs text-gray-500">{formatDate(req.requested_at)}</span>
                      </div>
                      {req.general_comment && <p className="text-sm text-gray-700">{req.general_comment}</p>}
                      {req.field_comments && Object.keys(req.field_comments).length > 0 && (
                        <div className="mt-2 text-xs space-y-1">
                          {Object.entries(req.field_comments).map(([field, comment]) => (
                            <div key={field}><span className="font-medium capitalize">{field.replace(/_/g, ' ')}:</span> <span className="text-gray-600">{comment}</span></div>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            )}
            {(!data.audit_trail || data.audit_trail.length === 0) && (!data.status_history || data.status_history.length === 0) && (
              <div className="text-center py-8 text-gray-500"><History className="h-12 w-12 mx-auto mb-2 text-gray-300" /><p>No audit trail available yet</p></div>
            )}
          </div>
        )}
        <DialogFooter><Button variant="outline" onClick={() => onOpenChange(false)}>Close</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
